import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { newOperationCache } from '@chancenwerk/db/utils/cache.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { DateTime } from 'luxon';
import { PresenceRecord, SchuKoPresenceRecord, Status, STATUS_TO_CSV } from '../../db/PresenceRecord.js';
import { Course } from '../../db/courses/Course.js';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import type { Mitarbeiter } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '../../master-data-management/users/entities/Student.js';

@Entity()
export class AttendanceOverviewResult extends AdornisEntity {
  public static override _class = 'AttendanceOverviewResult';

  @Field(type => DateTime)
  public lastAttendance: Maybe<DateTime>;

  @Field(type => Int)
  public attendanceCount: Maybe<number>;
}

const getAttendanceOverview = registerQuery({
  type: () => AttendanceOverviewResult,
  operationName: 'AttendanceAPI_getOverview',
  params: [{ type: () => String, name: 'userID' }],
  resolve: (userID: string) => {
    return async (gqlFields: BaseQLSelectionSet<AttendanceOverviewResult>) => {
      if (!context.serverContext) {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
          roles: { name: 1, permissions: 1, contexts: 1 },
        });

        if (!user) {
          throw new Error('User not found');
        }
      }

      const presRecordCol = await getRawCollection<PresenceRecord>(PresenceRecord._collectionName);
      const schukoRecordCol = await getRawCollection<SchuKoPresenceRecord>(SchuKoPresenceRecord._collectionName);

      // Use aggregation to get only existing courses with attendance records for this user
      const courseIdsAggregation = await presRecordCol
        .aggregate([
          {
            $match: {
              $or: [
                { 'studentRecordsUS.userID': userID },
                { 'studentRecordsOS.userID': userID },
                { 'lekoRecords.userID': userID },
              ],
              courseID: { $exists: true, $ne: null },
            },
          },
          {
            $lookup: {
              from: Course._collectionName,
              localField: 'courseID',
              foreignField: '_id',
              as: 'course',
            },
          },
          {
            $match: {
              course: { $ne: [] },
            },
          },
          {
            $project: {
              _id: 1,
            },
          },
        ])
        .toArray();

      const validRecordIds = courseIdsAggregation.map(r => r._id);

      // Parallel queries for both record types with optimized filtering
      const [presenceResults, schukoResults] = await Promise.all([
        // Get presence records with attendance stats in a single query
        presRecordCol
          .aggregate([
            {
              $match: {
                _id: { $in: validRecordIds },
                $or: [
                  { studentRecordsUS: { $elemMatch: { userID: userID, status: Status.PRESENT } } },
                  { studentRecordsOS: { $elemMatch: { userID: userID, status: Status.PRESENT } } },
                  { lekoRecords: { $elemMatch: { userID: userID, status: Status.PRESENT } } },
                ],
              },
            },
            {
              $project: {
                date: 1,
                hasPresent: {
                  $cond: {
                    if: {
                      $or: [
                        {
                          $anyElementTrue: {
                            $map: {
                              input: '$studentRecordsUS',
                              as: 'record',
                              in: {
                                $and: [
                                  { $eq: ['$$record.userID', userID] },
                                  { $eq: ['$$record.status', Status.PRESENT] },
                                ],
                              },
                            },
                          },
                        },
                        {
                          $anyElementTrue: {
                            $map: {
                              input: '$studentRecordsOS',
                              as: 'record',
                              in: {
                                $and: [
                                  { $eq: ['$$record.userID', userID] },
                                  { $eq: ['$$record.status', Status.PRESENT] },
                                ],
                              },
                            },
                          },
                        },
                        {
                          $anyElementTrue: {
                            $map: {
                              input: '$lekoRecords',
                              as: 'record',
                              in: {
                                $and: [
                                  { $eq: ['$$record.userID', userID] },
                                  { $eq: ['$$record.status', Status.PRESENT] },
                                ],
                              },
                            },
                          },
                        },
                      ],
                    },
                    then: 1,
                    else: 0,
                  },
                },
              },
            },
            {
              $group: {
                _id: null,
                lastAttendance: { $max: '$date' },
                attendanceCount: { $sum: '$hasPresent' },
              },
            },
          ])
          .toArray(),

        // Get SchukO records with attendance stats in a single query
        schukoRecordCol
          .aggregate([
            {
              $match: {
                'schukoRecords.userID': userID,
                'schukoRecords.status': Status.PRESENT,
              },
            },
            {
              $project: {
                date: 1,
                hasPresent: {
                  $cond: {
                    if: {
                      $anyElementTrue: {
                        $map: {
                          input: '$schukoRecords',
                          as: 'record',
                          in: {
                            $and: [{ $eq: ['$$record.userID', userID] }, { $eq: ['$$record.status', Status.PRESENT] }],
                          },
                        },
                      },
                    },
                    then: 1,
                    else: 0,
                  },
                },
              },
            },
            {
              $group: {
                _id: null,
                lastAttendance: { $max: '$date' },
                attendanceCount: { $sum: '$hasPresent' },
              },
            },
          ])
          .toArray(),
      ]);

      // Extract results or use defaults
      const presenceStats = presenceResults[0] || { lastAttendance: null, attendanceCount: 0 };
      const schukoStats = schukoResults[0] || { lastAttendance: null, attendanceCount: 0 };

      // Determine the latest attendance date
      let lastAttendanceBoth: Maybe<Date> = null;
      if (presenceStats.lastAttendance && schukoStats.lastAttendance) {
        lastAttendanceBoth = DateTime.max(
          DateTime.fromJSDate(presenceStats.lastAttendance),
          DateTime.fromJSDate(schukoStats.lastAttendance),
        ).toJSDate();
      } else {
        lastAttendanceBoth = presenceStats.lastAttendance || schukoStats.lastAttendance;
      }

      // Return the combined results
      return new AttendanceOverviewResult({
        lastAttendance: lastAttendanceBoth,
        attendanceCount: (presenceStats.attendanceCount || 0) + (schukoStats.attendanceCount || 0),
      });
    };
  },
});

const exportAttendanceMonth = registerQuery({
  type: () => String,
  operationName: 'exportAttendanceMonth',
  resolve: (month: DateTime) => {
    return async (gqlFields: void) => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const userCache = newOperationCache<ChancenwerkUser>(async (id: string): Promise<Maybe<ChancenwerkUser>> => {
        return ChancenwerkUser.getByID<ChancenwerkUser | Student | Mitarbeiter>(id)({
          _id: 1,
          _class: 1,
          vorname: 1,
          nachname: 1,
          email: 1,
          studentID: 1,
          employeeID: 1,
        });
      });

      const kpfCache = newOperationCache<KPF>(async (id: string): Promise<Maybe<KPF>> => {
        return KPF.getByID<KPF>(id)({
          _id: 1,
          kuerzel: 1,
          produkt: {
            kuerzel: 1,
          },
          school: {
            kuerzel: 1,
          },
        });
      });

      const courseCache = newOperationCache<Course>(async (id: string): Promise<Maybe<Course>> => {
        return Course.getByID<Course>(id)({
          _id: 1,
          _class: 1,
          startTime: 1,
          kpfID: 1,
        });
      });

      // * Find all records for the. month
      const normalRecordForMonth = await loadMongoData(PresenceRecord, {
        date: {
          $gte: month.startOf('month').toJSDate(),
          $lte: month.endOf('month').toJSDate(),
        },
      });
      const schukoRecordForMonth = await loadMongoData(SchuKoPresenceRecord, {
        date: {
          $gte: month.startOf('month').toJSDate(),
          $lte: month.endOf('month').toJSDate(),
        },
      });

      // * Collect all the date cells and sort them from earliest to latest
      const dateCellsMapped: Record<
        string,
        {
          date: DateTime;
        }
      > = {};

      for (const record of normalRecordForMonth) {
        if (!record.courseID) {
          continue;
        }

        const course = await courseCache.get(record.courseID);
        if (!course) {
          continue;
        }

        const date = DateTime.fromJSDate(record.date).set({
          hour: course.startTime?.hour,
          minute: course.startTime?.minute,
        });
        const key = date.toFormat('dd.MM.yyyy_HH:mm');
        if (!dateCellsMapped[key]) {
          dateCellsMapped[key] = { date };
        }
      }

      // * Sort the date cells by date and map them to the index of the date cell for faster access
      const dateCells = Object.values(dateCellsMapped).sort((a, b) => a.date.toMillis() - b.date.toMillis());
      const dateCellKeyToIndex = dateCells.reduce((acc, curr, index) => {
        acc[curr.date.toFormat('dd.MM.yyyy_HH:mm')] = index;
        return acc;
      }, {} as Record<string, number>);
      const dateCellKeyToIndexSchuko = dateCells.reduce((acc, curr, index) => {
        if (acc[curr.date.toFormat('dd.MM.yyyy')]) {
          acc[curr.date.toFormat('dd.MM.yyyy')]?.push(index);
        } else {
          acc[curr.date.toFormat('dd.MM.yyyy')] = [index];
        }
        return acc;
      }, {} as Record<string, number[]>);

      // * Build CSV header
      const header = [
        'ID',
        'Nachname',
        'Vorname',
        'KPF',
        'Typ',
        ...dateCells.map(cell => cell.date.toFormat('dd.MM.yyyy_HH:mm')),
      ];

      // * Build the user lines
      const userLines: Record<
        string,
        {
          infos: string[];
          dates: Record<string, string[]>;
        }
      > = {};

      // * Build the user lines for the normal records
      for (const record of normalRecordForMonth) {
        if (!record.courseID) {
          continue;
        }

        const course = await courseCache.get(record.courseID);
        if (!course) {
          continue;
        }

        const kpf = await kpfCache.get(course.kpfID);
        if (!kpf) {
          continue;
        }

        const date = DateTime.fromJSDate(record.date).set({
          hour: course.startTime?.hour,
          minute: course.startTime?.minute,
        });
        const dateKey = date.toFormat('dd.MM.yyyy_HH:mm');
        const dateIndex = dateCellKeyToIndex[dateKey] ?? -1;
        if (dateIndex === -1) {
          continue;
        }

        const userRecords = [
          ...(record.studentRecordsOS ?? []),
          ...(record.studentRecordsUS ?? []),
          ...(record.lekoRecords ?? []),
        ];
        for (const userRecord of userRecords) {
          if (!userRecord.userID) {
            continue;
          }

          const user = await userCache.get(userRecord.userID);
          if (!user) {
            continue;
          }

          const userLine = (userLines[user._id] ??= {
            infos: [
              (user as any).studentID ?? (user as any).employeeID ?? '',
              user.nachname ?? '',
              user.vorname ?? '',
              'KPF',
              user._class === Student._class ? 'Schüler:in' : 'LeKo',
            ],
            dates: {},
          });

          userLine.dates[kpf.kuerzel] ??= new Array(dateCells.length).fill('');
          if (userRecord.status) {
            userLine.dates[kpf.kuerzel]![dateIndex] = STATUS_TO_CSV[userRecord.status];
          }
        }
      }

      // * Build the lines for schuko records
      for (const record of schukoRecordForMonth) {
        if (!record.kpfID) {
          continue;
        }

        const kpf = await kpfCache.get(record.kpfID);
        if (!kpf) {
          continue;
        }

        const date = DateTime.fromJSDate(record.date);
        const prefix = date.toFormat('dd.MM.yyyy');
        const matchingDateCells = dateCellKeyToIndexSchuko[prefix];
        if (!matchingDateCells) {
          continue;
        }

        for (const userRecord of record.schukoRecords ?? []) {
          if (!userRecord.userID) {
            continue;
          }

          const user = await userCache.get(userRecord.userID);
          if (!user) {
            continue;
          }

          const userLine = (userLines['SCHUKO_' + user._id] ??= {
            infos: [
              (user as any).studentID ?? (user as any).employeeID ?? '',
              user.nachname ?? user.email ?? '',
              user.vorname ?? '',
              'KPF',
              'SchuKo',
            ],
            dates: {},
          });

          userLine.dates[kpf.kuerzel] ??= new Array(dateCells.length).fill('');
          matchingDateCells.forEach(dateIndex => {
            if (!userRecord.status) {
              return;
            }
            userLine.dates[kpf.kuerzel]![dateIndex] = STATUS_TO_CSV[userRecord.status];
          });
        }
      }

      const lines = Object.values(userLines).flatMap(userLine => {
        const byKpf = Object.entries(userLine.dates);
        return byKpf.map(([kpf, lines]) => {
          const infos = [...userLine.infos, ...lines];
          infos[3] = kpf;
          return infos;
        });
      });

      return header.join(';') + '\n' + lines.map(line => line.join(';')).join('\n');
    };
  },
  params: [{ type: () => DateTime, name: 'month' }],
});

export namespace AttendanceAPI {
  export const getOverview = getAttendanceOverview;
  export const exportMonth = exportAttendanceMonth;
}
